/**
 * Parse text or voice input to extract deal information
 * Supports both Arabic and English input
 */

// Property type keywords (Arabic and English)
const propertyTypes = {
  apartment: ['شقة', 'شقه', 'شقق', 'apartment', 'flat', 'unit', 'شقة سكنية'],
  villa: ['فيلا', 'villa', 'house', 'منزل', 'قصر'],
  land: ['أرض', 'أراضي', 'land', 'plot', 'lot', 'قطعة أرض'],
  commercial: ['تجاري', 'محل', 'مكتب تجاري', 'commercial', 'shop', 'store'],
  office: ['مكتب', 'مكاتب', 'office', 'offices'],
  other: ['أخرى', 'other']
};

// Deal type keywords
const dealTypes = {
  sale: ['بيع', 'شراء', 'sale', 'buy', 'purchase', 'sold'],
  rent: ['إيجار', 'تأجير', 'rent', 'rental', 'lease', 'leased']
};

// Status keywords
const statusTypes = {
  completed: ['مكتمل', 'منتهي', 'completed', 'done', 'finished', 'closed'],
  in_progress: ['قيد التنفيذ', 'in progress', 'in-progress'],
  pending: ['قيد', 'معلق', 'pending', 'waiting']
};

/**
 * Extract property type from text
 * Handles Arabic text without case conversion
 */
const extractPropertyType = (text) => {
  // For Arabic text, we need to search without lowercasing (Arabic doesn't have case)
  // For English, we can lowercase
  const normalizedText = text;
  
  for (const [type, keywords] of Object.entries(propertyTypes)) {
    for (const keyword of keywords) {
      // Check both original and lowercase versions
      if (normalizedText.includes(keyword) || normalizedText.toLowerCase().includes(keyword.toLowerCase())) {
        return type;
      }
    }
  }
  return '';
};

/**
 * Extract deal type from text
 * Handles Arabic text properly
 */
const extractDealType = (text) => {
  const normalizedText = text;
  
  for (const [type, keywords] of Object.entries(dealTypes)) {
    for (const keyword of keywords) {
      // Check both original and lowercase versions
      if (normalizedText.includes(keyword) || normalizedText.toLowerCase().includes(keyword.toLowerCase())) {
        return type;
      }
    }
  }
  return 'sale'; // Default to sale
};

/**
 * Extract price from text
 * Enhanced to handle Arabic numerals, small numbers with context (k, ألف, etc.), and time periods
 */
const extractPrice = (text) => {
  // Convert Arabic numerals to Western
  const arabicToWestern = {
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
  };
  let normalizedText = text;
  for (const [arabic, western] of Object.entries(arabicToWestern)) {
    normalizedText = normalizedText.replace(new RegExp(arabic, 'g'), western);
  }

  // Check for time period context (per year, per month, etc.)
  const hasYearContext = /(?:في السنة|في العام|per year|yearly|annually)/i.test(normalizedText);
  const hasMonthContext = /(?:في الشهر|شهرياً|per month|monthly)/i.test(normalizedText);
  
  // Enhanced price patterns - now includes smaller numbers with context
  const pricePatterns = [
    // Numbers with currency indicators
    /(\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?)\s*(?:ريال|ريال|sar|ريال سعودي|riyal|ريالاً)/gi,
    /(?:ريال|ريال|sar|ريال سعودي|riyal|ريالاً|سعر|price|بسعر)\s*(\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?)/gi,
    /(?:بمبلغ|قيمة|amount)\s*(\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?)/gi,
    // Numbers with k, ألف, thousand
    /(\d{1,3}(?:\.\d+)?)\s*(?:k|ألف|thousand|ألف ريال|k sar)/gi,
    /(\d{1,3}(?:\.\d+)?)\s*(?:في السنة|في العام|per year|yearly)/gi,
    /(\d{1,3}(?:\.\d+)?)\s*(?:في الشهر|per month|monthly)/gi,
    // Small numbers (2-3 digits) with context
    /(\d{2,3})\s*(?:في السنة|في العام|per year|yearly|ألف|k)/gi,
    // Large numbers (4+ digits) - likely prices
    /(\d{4,})\s*(?:ريال|ريال|sar|riyal)?/gi,
    // Standalone numbers with time context nearby
    /(\d{1,4})\s*(?:ريال|ريال|sar)?/gi,
  ];

  const allNumbers = [];

  for (const pattern of pricePatterns) {
    const matches = normalizedText.match(pattern);
    if (matches && matches.length > 0) {
      matches.forEach(match => {
        let num = match.replace(/[^\d.]/g, '');
        let parsed = parseFloat(num.replace(/,/g, ''));
        
        // Handle k, ألف, thousand multipliers
        if (/k|ألف|thousand/i.test(match)) {
          parsed = parsed * 1000;
        }
        
        // Handle year context - multiply by 12 if it's monthly, or keep as is if yearly
        if (hasYearContext && !hasMonthContext) {
          // If it's "42 في السنة", it's already the yearly price
          // But if it's small like 42, it might be 42k
          if (parsed < 1000 && parsed > 0) {
            parsed = parsed * 1000; // Assume 42 means 42k
          }
        } else if (hasMonthContext) {
          // If it's monthly, multiply by 12 for yearly equivalent
          parsed = parsed * 12;
        }
        
        if (!isNaN(parsed) && parsed > 0 && parsed < 1000000000) { // Reasonable price range
          allNumbers.push(parsed);
        }
      });
    }
  }
  
  // Also look for numbers directly followed by "في السنة" or "في العام"
  // Pattern: number followed by "في السنة" or "في العام" or "per year"
  const numberWithYearPattern = /(\d{1,4})\s*(?:في السنة|في العام|per year|yearly)/gi;
  const yearMatches = normalizedText.match(numberWithYearPattern);
  if (yearMatches) {
    yearMatches.forEach(match => {
        const num = match.replace(/[^\d.]/g, '');
      let parsed = parseFloat(num);
      if (!isNaN(parsed) && parsed > 0) {
        // If it's a small number (like 42), assume it means 42k (42,000)
        if (parsed < 1000) {
          parsed = parsed * 1000;
        }
        // If it's already large, keep it as is (might be 42000 already)
        if (parsed > 0 && parsed < 1000000000) {
          allNumbers.push(parsed);
        }
      }
    });
  }
  
  // Also check for numbers near "في السنة" even if not directly adjacent
  if (hasYearContext && allNumbers.length === 0) {
    const allNums = normalizedText.match(/\d{1,4}/g);
    if (allNums) {
      allNums.forEach(numStr => {
        let parsed = parseFloat(numStr);
        if (parsed > 0 && parsed < 1000) {
          parsed = parsed * 1000; // Assume small numbers with year context are in thousands
          if (parsed < 1000000000) {
            allNumbers.push(parsed);
          }
        }
      });
    }
  }
  
  // Handle monthly context
  if (hasMonthContext && allNumbers.length === 0) {
    const allNums = normalizedText.match(/\d{1,4}/g);
    if (allNums) {
      allNums.forEach(numStr => {
        let parsed = parseFloat(numStr);
        if (parsed > 0) {
          parsed = parsed * 12; // Monthly to yearly
          if (parsed < 1000000000) {
            allNumbers.push(parsed);
          }
        }
      });
    }
  }
  
  if (allNumbers.length > 0) {
    // Get the largest number (likely the price)
    const maxPrice = Math.max(...allNumbers);
        // Format with commas
        return maxPrice.toLocaleString('en-US');
      }
  
  return '';
};

/**
 * Extract date from text
 * Looks for date patterns
 */
const extractDate = (text) => {
  const datePatterns = [
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/, // YYYY-MM-DD or YYYY/MM/DD
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/, // DD-MM-YYYY or DD/MM/YYYY
    /(?:today|اليوم)/i,
    /(?:yesterday|أمس)/i
  ];

  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match) {
      if (match[0].toLowerCase().includes('today') || match[0].includes('اليوم')) {
        return new Date().toISOString().split('T')[0];
      }
      if (match[0].toLowerCase().includes('yesterday') || match[0].includes('أمس')) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
      }
      // Try to parse the date
      try {
        const dateStr = match[0];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        // Continue to next pattern
      }
    }
  }
  return '';
};

/**
 * Extract address from text
 * Looks for location keywords and extracts following text
 * Enhanced to handle more patterns and city names
 */
const extractAddress = (text) => {
  const addressKeywords = [
    'عنوان', 'address', 'location', 'مكان', 'في', 'at', 'located', 'موجود', 'ب',
    'الرياض', 'riyadh', 'جدة', 'jeddah', 'الدمام', 'dammam', 'مكة', 'makkah',
    'المدينة', 'madinah', 'الخبر', 'khobar', 'الطائف', 'taif', 'أبها', 'abha',
    'حائل', 'hail', 'نجران', 'najran', 'جازان', 'jazan', 'ينبع', 'yanbu',
    'بريدة', 'buraidah', 'تبوك', 'tabuk', 'القطيف', 'qatif'
  ];

  // Common neighborhoods and areas (check these first as they're more specific)
  const neighborhoods = [
    'الرمال', 'الملز', 'العليا', 'الروضة', 'النسيم', 'الورود', 'النهضة',
    'الخزامى', 'الربوة', 'الزهراء', 'السلام', 'الفلاح', 'السليمانية',
    'العقيق', 'البديعة', 'الشميسي', 'المرسلات', 'الغدير', 'الوادي'
  ];

  // Check for neighborhoods first (more specific)
  for (const neighborhood of neighborhoods) {
    if (text.includes(neighborhood)) {
      const index = text.indexOf(neighborhood);
      // Extract surrounding text including "في" before it
      const start = Math.max(0, index - 10);
      const end = Math.min(text.length, index + neighborhood.length + 20);
      let address = text.substring(start, end).trim();
      // Clean up - remove price numbers if they appear
      address = address.replace(/\d+\s*(?:في السنة|في العام|per year)/gi, '').trim();
      if (address.length > 3 && address.length < 200) {
        return address;
      }
    }
  }

  // Try to find city names
  const cities = [
    { ar: 'الرياض', en: 'riyadh' },
    { ar: 'جدة', en: 'jeddah' },
    { ar: 'الدمام', en: 'dammam' },
    { ar: 'مكة', en: 'makkah' },
    { ar: 'المدينة', en: 'madinah' },
    { ar: 'الخبر', en: 'khobar' }
  ];

  for (const city of cities) {
    const cityIndex = text.indexOf(city.ar);
    const cityIndexEn = text.toLowerCase().indexOf(city.en.toLowerCase());
    if (cityIndex !== -1 || cityIndexEn !== -1) {
      const index = cityIndex !== -1 ? cityIndex : cityIndexEn;
      // Extract surrounding text (before and after city name)
      const start = Math.max(0, index - 30);
      const end = Math.min(text.length, index + 50);
      const address = text.substring(start, end).trim();
      if (address.length > 5 && address.length < 200) {
        return address;
      }
    }
  }

  // Fallback to keyword-based extraction
  for (const keyword of addressKeywords) {
    const index = text.toLowerCase().indexOf(keyword.toLowerCase());
    if (index !== -1) {
      // Extract text after the keyword (up to 100 characters or next sentence)
      const afterKeyword = text.substring(index + keyword.length).trim();
      const address = afterKeyword.split(/[.\n،,]/)[0].trim();
      if (address.length > 5 && address.length < 200) {
        return address;
      }
    }
  }
  return '';
};

/**
 * Extract status from text
 */
const extractStatus = (text) => {
  const lowerText = text.toLowerCase();
  for (const [status, keywords] of Object.entries(statusTypes)) {
    for (const keyword of keywords) {
      if (lowerText.includes(keyword.toLowerCase())) {
        return status;
      }
    }
  }
  return 'pending'; // Default to pending
};

/**
 * Main function to parse text and extract deal information
 */
export const parseDealText = (text) => {
  if (!text || typeof text !== 'string') {
    return {
      property_type: '',
      property_address: '',
      deal_type: 'sale',
      price: '',
      deal_date: '',
      status: 'pending',
      notes: text || ''
    };
  }

  const parsed = {
    property_type: extractPropertyType(text),
    property_address: extractAddress(text),
    deal_type: extractDealType(text),
    price: extractPrice(text),
    deal_date: extractDate(text),
    status: extractStatus(text),
    notes: text
  };

  return parsed;
};

/**
 * Format extracted data for display
 */
export const formatParsedDeal = (parsed) => {
  return {
    ...parsed,
    hasData: !!(parsed.property_type || parsed.price || parsed.property_address || parsed.deal_date)
  };
};

